import { useCallback, useMemo, useState } from 'react';
import cls from 'clsx';
import { useLocation, matchPath } from 'react-router';
import { Left, Right } from '@linktivity/link-icons';
import { Menu } from '@linktivity/link-ui';
import { SidebarMenuProps, MenuItem } from './SidebarMenu.types';
import styles from './menu.module.css';

const SidebarMenu: React.FC<SidebarMenuProps> = ({
  menuItems,
  defaultExpandedMenu,
  bottomItems = [] // 新增 bottomItems，默认空数组
}) => {
  const { pathname } = useLocation();

  const [collapsed, setCollapsed] = useState(false);

  const toggleCollapsed = useCallback(() => {
    setCollapsed(prev => !prev);
  }, []);

  // 计算当前 URL 匹配的 SubMenu
  const activeSubMenu = useMemo(() => {
    return menuItems.find(menu =>
      menu.children?.some(
        child =>
          child.to && matchPath({ path: child.to, end: !!child.end }, pathname)
      )
    );
  }, [menuItems, pathname]);

  // 渲染单个菜单项（复用在顶部和底部）
  const renderMenuItem = (item: MenuItem) => {
    // 1. Action 类型：直接渲染自定义元素
    if (item.action) {
      return (
        <li
          key={item.label}
          className={cls(styles.actionWrapper, {
            [styles.archivedOff]: item.archived
          })}
        >
          {item.action}
        </li>
      );
    }

    // 2. 有子菜单的情况
    if (item.children) {
      return (
        <Menu.SubMenu
          key={item.label}
          label={item.label}
          Icon={item.Icon}
          name={item.name}
          className={item.archived ? styles.archivedOff : ''}
        >
          {item.children.map(renderMenuItem)}
        </Menu.SubMenu>
      );
    }

    // 3. 普通菜单项
    return (
      <Menu.MenuItem
        key={item.label}
        label={item.label}
        Icon={item.Icon}
        to={item.to}
        disabled={item.to ? false : true}
        end={item.end}
        className={item.archived ? styles.archivedOff : ''}
      />
    );
  };

  const hasBottomItems = useMemo(() => bottomItems.length > 0, [bottomItems]);

  return (
    <aside className={cls(styles.sidebar, { [styles.collapsed]: collapsed })}>
      <button
        type="button"
        className={styles.toggle}
        onClick={toggleCollapsed}
        aria-label={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
        aria-expanded={!collapsed}
      >
        {collapsed ? (
          <Right width={16} height={16} />
        ) : (
          <Left width={16} height={16} />
        )}
      </button>
      <div className={styles.nav}>
        <div className={styles.inner}>
          <Menu
            className={styles.menu}
            accordion
            defaultExpandedMenu={[
              ...(defaultExpandedMenu || []),
              ...(activeSubMenu?.name ? [activeSubMenu.name] : [])
            ]}
          >
            {menuItems.map(renderMenuItem)}
          </Menu>
        </div>
      </div>
      {hasBottomItems && (
        <div className={styles.bottom}>
          <Menu className={styles.menu} accordion={false}>
            {bottomItems.map(renderMenuItem)}
          </Menu>
        </div>
      )}
    </aside>
  );
};

export default SidebarMenu;
